// FP 보정 간트 — 휴무(다운타임) × 분할타입(`divide_tp_cd`) 규칙 (순수 함수 모듈)
//
// 엔진(`DateTimePeriods.forwardDateTimePeriod`)이 계획 시 쓰는 규칙을 **미리보기 수준으로** 옮긴 것이다.
// old FP 서버 `com.zionex.t3series.fp.time.DivideType` 은 Y/N/I 세 값이고, 배치 함수가 값에 따라
// 갈린다(`forwardDateTimePeriodY` / `...N` / `...I`).
//
//   Y (기본) : 가동 구간을 **이어붙여** 소요를 채운다 → 휴무를 품으면 구간이 그만큼 늘어난다
//   N        : 휴무에 걸리지 않는 **연속 가동 구간**에만 놓을 수 있다 → 걸치면 배치 실패
//   I        : 휴무를 **관통**한다 → 길이 불변. ⚠️ **"휴무를 무시한다"가 아니다** —
//              시작은 휴무 안에 놓을 수 없고(다음 가동구간 시작으로 밀린다), **종료는 휴무 안에
//              놓일 수 있다.** 휴무 시작 지점에서 시작하는 것은 `I` 만 허용된다(§7-38).
//
// ⚠️ **이건 미리보기다. 정본은 저장 후 재계획이다.**
//   엔진은 여기 없는 것들을 더 본다 — 1초 미만 구간 절삭(`diff >= 1000L`), 자원 경합, 선후 제약,
//   대체자원, setup/queue/wait/move 의 개별 배치. 화면은 "대충 이렇게 된다"까지만 보여주고
//   정확한 값은 저장 후 엔진이 정한다. 그래서 이 모듈은 **오차를 감수하는 대신 단순함을 택했다**.
//
// ⚠️ 분할타입은 **활동의 속성이지 상태가 아니다.** 마스터(route/bor/wo_bor)에서 결정돼
//   `tb_fp_activity.divide_tp_cd` 로 복사되므로(`ActivitySaveRunner:140`
//   `entity.setDivideTpCd(activity.getBor().getDivideType().name())`), 사용자가 작업을 옮겨도
//   값이 바뀌지 않는다. JC 박스는 **자기 divide type 을 따로 갖는다**(`prev_jc_divide_tp_cd` /
//   `next_jc_divide_tp_cd`) — 본 활동이 Y라도 JC는 N일 수 있다.

export const DIVIDE_SPLIT = "Y";     // 분할 가능 — 휴무를 건너뛰며 채움
export const DIVIDE_NONSTOP = "N";   // 분할 불가 — 휴무를 피해야 함
export const DIVIDE_IGNORE = "I";    // 휴무 무시 — 관통

/**
 * 박스 위에 **빗금(분할 마크)을 칠할 구간** — 분할타입에 따라 갈린다(§7-37).
 *
 *   Y : 휴무를 **품을 수 있지만 그 동안은 작업이 아니다** → 겹치는 만큼 빗금
 *   N : 휴무와 겹칠 수 없다 → 없음
 *   I : 휴무를 **관통하고 그 시간도 작업으로 본다** → 없음(바가 그대로 보인다)
 *
 * ⚠️ **서버의 `divideYDowntimes` 를 대체한다.** 그 값은 ① `activity.startTs < d.startTs AND
 * d.endTs < activity.endTs` 로 **완전히 포함되는 휴무만** 뽑아 **부분 겹침이 빠지고**
 * ② 계획 당시 좌표 기준이라 **편집하면 곧바로 낡는다**(작업을 휴무로 옮겨도 빗금이 안 생겼다 —
 * 2026-09-10 신고). 여기서 계산하면 둘 다 해결되고 **드래그 미리보기에도 그대로 따라온다.**
 *
 * ⚠️ **JC 띠는 제외한다.** JC 는 작업이 아니라 준비·교체 시간이고, 자기 분할타입을 따로 갖는다
 * (`prev_jc_divide_tp_cd`/`next_jc_divide_tp_cd`) — 박스에 실린 `activity.divideTpCd` 는 본 활동
 * 값이라 그대로 쓰면 틀린다.
 *
 * ⚠️ `downtimes` 는 **정렬·병합된 상태**여야 한다(`normalizeDowntimes` 를 거친 값). 매 프레임
 * 불리는 경로라 여기서 다시 정규화하지 않고, 대신 시작 인덱스를 **이진탐색**한다.
 *
 * @returns `[{ s, e, box }]` — `box` 는 검색 흐리기 판정에 쓴다(`drawDivideMarks`)
 */
export const divideMarksOf = (boxes = [], downtimes = []) => {
  if (!boxes.length || !downtimes.length) return [];

  const marks = [];
  boxes.forEach(box => {
    // ⚠️ **JC 도 대상이다**(2026-09-15). JC 는 자기 분할타입을 따로 갖고(`prev_jc_divide_tp_cd`),
    // `Y` 면 활동과 똑같이 휴무를 품어 span 이 부풀어 오른다 — 57h 짜리 검정 띠가 실제로는
    // 2h 라는 사실이 빗금 없이는 화면에 전혀 안 나온다. 규칙이 같으니 표현도 같아야 한다.
    // `N`·`I` 는 아래 분할타입 필터가 알아서 걸러낸다(활동과 같은 이유).
    if (divideTypeOf(box) !== DIVIDE_SPLIT) return;
    if (!(box.e > box.s)) return;                       // 결품(shtg) 등 0폭

    // 끝이 `box.s` 를 넘어서는 첫 휴무. 정렬돼 있으므로 거기서부터 훑으면 된다.
    let lo = 0;
    let hi = downtimes.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (downtimes[mid].e > box.s) hi = mid;
      else lo = mid + 1;
    }

    for (let i = lo; i < downtimes.length && downtimes[i].s < box.e; i += 1) {
      const s = Math.max(box.s, downtimes[i].s);
      const e = Math.min(box.e, downtimes[i].e);
      if (e > s) marks.push({ s, e, box });
    }
  });

  return marks;
};

/** 값이 없으면 `Y`. 엔진 `DivideType.getDefaultDivisibility()` 와 같다. */
export const divideTypeOf = (box) => {
  const raw = String(box?.activity?.divideTpCd ?? "").trim().toUpperCase();
  return raw === DIVIDE_NONSTOP || raw === DIVIDE_IGNORE ? raw : DIVIDE_SPLIT;
};

/** 시작 오름차순 정렬 + 맞닿거나 겹치는 구간 병합. 아래 계산들이 정렬·비중첩을 전제한다. */
export const normalizeDowntimes = (downtimes = []) => {
  const sorted = downtimes
    .filter(dt => Number.isFinite(dt?.s) && Number.isFinite(dt?.e) && dt.e > dt.s)
    .sort((a, b) => a.s - b.s);

  const merged = [];
  sorted.forEach(dt => {
    const last = merged[merged.length - 1];
    if (last && dt.s <= last.e) {
      last.e = Math.max(last.e, dt.e);
    } else {
      merged.push({ s: dt.s, e: dt.e });
    }
  });
  return merged;
};

/** `[s, e)` 와 휴무의 교집합 총량(ms). */
export const downtimeOverlapMs = (s, e, downtimes = []) => downtimes
  .reduce((sum, dt) => sum + Math.max(0, Math.min(e, dt.e) - Math.max(s, dt.s)), 0);

/** `[s, e)` 가 휴무와 조금이라도 겹치나. */
export const overlapsDowntime = (s, e, downtimes = []) =>
  downtimes.some(dt => s < dt.e && dt.s < e);

/**
 * 박스의 **순수 소요시간**(휴무를 뺀 실작업 길이).
 *
 * ⚠️ `process_time` 컬럼을 쓰지 않는다. 실측 결과 그 값은 **휴무가 이미 반영된 값**이었다
 * (2026-09-08: `process_time` 3,060분 = 구간 3,060분, 그중 겹친 휴무 2,880분 → 순수 180분).
 * 그래서 구간에서 휴무 교집합을 빼는 방식만 맞다.
 *
 * ⚠️ `divideYDowntimes`(서버가 내려주는 박스 내부 마크)를 쓰면 안 된다 — 그건
 * `activity.startTs < d.startTs AND d.endTs < activity.endTs` 로 **완전히 포함되는 휴무만**
 * 뽑은 것이라, 활동이 휴무 중간에서 시작·종료하는 **부분 겹침이 빠진다**. 행 전체 `downtimes` 로
 * 정확한 교집합을 구해야 한다.
 */
export const netDurationOf = (box, downtimes = []) => {
  const span = box.e - box.s;
  if (divideTypeOf(box) !== DIVIDE_SPLIT) return span;   // N·I 는 애초에 늘어나지 않는다
  return Math.max(0, span - downtimeOverlapMs(box.s, box.e, downtimes));
};

/**
 * 박스 배열에 **순수 소요(`netMs`)를 한 번 계산해 얹는다.**
 *
 * ⚠️ **이동 전 좌표에서 구해야 한다.** 옮긴 뒤에 구하면 늘어난 span 을 순수로 오인해서
 * (예: 휴무 2h 를 품은 [0,10) 을 휴무 없는 20시로 옮기면 span 10h → net 10h)
 * **길이가 영영 줄어들지 않는다** — 2026-09-08 실제로 밟은 버그다.
 *
 * `netMs` 는 이동에 대해 불변이므로 로드 시 한 번 붙여두면 여러 번 옮겨도 안정적이고,
 * 행간 이동에서도 **출발 자원의 휴무 기준**으로 구한 값이 그대로 따라간다(맞는 동작이다 —
 * 그 작업이 실제로 몇 시간짜리인지는 어느 자원으로 옮기든 같다).
 *
 * 리사이즈는 예외다 — 사용자가 구간 자체를 바꾸는 조작이라 그 시점에 **다시 붙여야** 한다.
 */
export const attachNetDuration = (boxes = [], downtimes = []) => {
  const dts = normalizeDowntimes(downtimes);
  return boxes.map(box => ({ ...box, netMs: netDurationOf(box, dts) }));
};

/**
 * `start` 부터 **가동 시간** `netMs` 를 채웠을 때의 종료 시각 — 엔진 `forwardDateTimePeriodY` 의
 * 미리보기판이다. 휴무를 만나면 건너뛰고 계속 채운다.
 *
 * `netMs`가 0이면 `start`를 그대로 돌려준다(0폭 박스는 위에서 걸러진다).
 * 휴무가 무한히 이어지는 일은 없으므로 루프는 항상 종료한다(마지막 휴무 뒤는 전부 가동).
 */
export const fillForward = (start, netMs, downtimes = []) => {
  if (!(netMs > 0)) return start;

  let cursor = start;
  let remain = netMs;

  // 정렬돼 있으므로 앞에서부터 한 번만 훑으면 된다.
  for (const dt of downtimes) {
    if (dt.e <= cursor) continue;          // 이미 지나친 휴무
    if (dt.s >= cursor + remain) break;    // 남은 작업이 이 휴무 전에 끝난다

    if (dt.s > cursor) remain -= (dt.s - cursor);   // 휴무 전까지 채우고
    cursor = dt.e;                                   // 휴무를 건너뛴다
  }

  return cursor + remain;
};

/**
 * **시작점을 휴무 밖으로 민다** — 휴무 안이면 그 휴무의 **끝**으로(항상 앞으로).
 * old TreeGrid 의 `Restrict start at excluded dates` 에 대응하고, 엔진 불변식과도 같다:
 * `Y` 활동의 시작·종료는 언제나 가동 시간에 놓인다
 * (`DateTimePeriods.getStartIndexes` 가 start 를 품은 휴무로 되돌린 뒤 `forwardDateTimePeriodY` 가
 * `diff < 1000L` 로 건너뛰어 `before = 휴무 끝` 을 시작으로 삼는다. 2026-09-09 엔진 코드 확인).
 *
 * **이게 없으면 휴무 안에서 end 가 고정된다** — `fillForward` 가 시작점을 휴무 끝으로 밀어 올리므로
 * start 를 휴무 안 어디에 두든 `end = 휴무끝 + 순수` 로 같아진다. 화면에는 "시작만 오른쪽으로
 * 밀리고 끝은 그대로"로 보인다(2026-09-08 신고 증상).
 *
 * **항상 앞으로 민다** — 엔진과 같은 방향이다. 한때 조작 방향을 따라 뒤(밴드 시작)로도 밀어 봤는데,
 * 그러면 시작이 밴드 시작에 붙고 **그 구간에 실제 작업은 0인** 박스가 나온다. 엔진은 그런 배치를
 * 만들지 않으므로(위 `diff < 1000L`) 저장 후 재계획에서 조용히 되돌아간다 — 화면에서는
 * "시작만 늘어나고 종료는 그대로"로 보였다(2026-09-09 신고).
 *
 * 조작 방향에 따른 반응은 여기가 아니라 **입력 쪽**에서 해결한다 — `shiftDragStart` 가 드래그
 * 목표를 밴드 **직전**에 세워서 넘기므로, 여기까지 밴드 안 시작이 내려오는 건 스윕에 밀린 이웃뿐이다.
 *
 * ⚠️ 이 제약은 **이동 대상과 밀려난 이웃을 구별하지 않는다.** 한때 스윕 **전에** 이동 대상에만
 * 걸었더니, 왼쪽 드래그에서 이동 대상이 밴드를 통째로 삼키고 슬라이드 압착이 **앞 작업을 밴드
 * 너머로 밀어내** 정작 늘어나야 할 앞 작업은 멀쩡했다(2026-09-09 신고). `applyDivideRules` 의
 * 순차 스윕 안에서 걸면 **밴드에 먼저 닿는 박스**가 자연히 그 몫을 받는다.
 */
export const pushStartOutOfDowntime = (start, downtimes = []) => {
  const hit = downtimes.find(dt => start > dt.s && start < dt.e);
  return hit ? hit.e : start;
};

/** 시작시각이 휴무 **안쪽**(양 끝 제외)인가. `I` 가 설 수 없는 자리다(§7-38). */
export const startsInsideDowntime = (start, downtimes = []) =>
  downtimes.some(dt => start > dt.s && start < dt.e);

/** `fillForward` 의 거울상 — `end` 에서 뒤로 `netMs` 만큼 채웠을 때의 시작 시각. */
export const fillBackward = (end, netMs, downtimes = []) => {
  if (!(netMs > 0)) return end;

  let cursor = end;
  let remain = netMs;

  for (let i = downtimes.length - 1; i >= 0; i -= 1) {
    const dt = downtimes[i];
    if (dt.s >= cursor) continue;             // 아직 지나지 않은(뒤쪽) 휴무
    if (dt.e <= cursor - remain) break;       // 남은 작업이 이 휴무 뒤에서 끝난다

    if (dt.e < cursor) remain -= (cursor - dt.e);
    cursor = dt.s;
  }

  return cursor - remain;
};

/** 드래그 격자의 기본 간격(1시간). `adjustShrink.SNAP_MS` 와 같은 값이지만, 이 모듈은
 *  의존이 없는 순수 모듈이라 import 하지 않고 인자로 받는다(화면이 항상 명시적으로 넘긴다). */
export const SNAP_DEFAULT_MS = 60 * 60 * 1000;

/** `anchor` 기준 격자에서 `limit` **미만** 최대 격자점. 분·초·ms가 보존된다(§7-15e 변위 스냅). */
const latticeBefore = (anchor, limit, snap) =>
  anchor + Math.ceil((limit - anchor) / snap - 1) * snap;

/**
 * 시작시각이 휴무 안이면 **밴드 직전 격자점으로 되돌린다** — 앞으로 밀지 않는다.
 *
 * `pushStartOutOfDowntime`(밴드 **끝**으로 앞으로 밈)의 형제지만 방향이 반대다. 저쪽은
 * `applyDivideRules` 의 스윕용 — **밀려난 이웃**을 합법 위치로 정규화한다. 이쪽은 **드래그 입력**용
 * 이다: 사용자가 갈 수 없는 곳을 가리켰을 때 커서를 앞질러 가지 않고 **갈 수 있는 가장 가까운 자리에
 * 세워 둔다**(§7-29 규칙 5 "제약에 걸리면 튕기지 말고 벽에 붙인다"와 같은 계열).
 *
 * **`hit.s` 자체가 아니라 그 직전 격자점**이다. 시작이 밴드 시작에 딱 붙으면 그 구간에 실제 작업이
 * 0인 박스가 되는데, 엔진은 그런 배치를 만들지 않아(`forwardDateTimePeriodY` 의 `diff < 1000L`)
 * 저장 후 재계획에서 조용히 밴드 끝으로 되돌아간다 — 미리보기가 거짓말을 하게 된다.
 * 한 칸 앞에 세우면 밴드 앞에 실제 작업이 남아 엔진이 유지한다.
 *
 * 격자는 `anchor`(= 드래그를 시작한 원래 시작시각) 기준이라 분·초·ms가 보존된다.
 * 밴드가 연달아 있으면 한 칸 물러난 자리가 또 다른 밴드일 수 있어 루프를 돈다
 * (`normalizeDowntimes` 가 맞닿은 밴드를 병합하므로 실제로는 격자 간격이 밴드보다 클 때만 반복된다).
 */
export const clampStartBeforeDowntime = (
  start, downtimes = [], anchor = start, snap = SNAP_DEFAULT_MS
) => {
  let cur = start;
  // 정렬돼 있고 `cur` 는 왼쪽으로만 가므로 **뒤에서부터 한 번만** 훑으면 된다
  // (물러난 자리가 또 밴드여도 그건 반드시 더 앞쪽 밴드라 같은 스캔에서 걸린다).
  for (let i = downtimes.length - 1; i >= 0; i -= 1) {
    const band = downtimes[i];
    if (cur >= band.s && cur < band.e) cur = latticeBefore(anchor, band.s, snap);
  }
  return cur;
};

/**
 * **드래그 변위를 벽시계 그대로 읽고, 휴무에 걸리면 밴드 앞에 세운다.**
 *
 * 시작시각은 휴무 안에 있을 수 없어서 밴드 폭만큼은 갈 곳이 없다. 그 폭을 어떻게 처리하느냐가
 * 이 함수의 전부이고, 세 번 바뀌었다(전부 실제 신고에서 나왔다):
 *
 *   ① 앞으로만 스냅   → 왼쪽 드래그가 밴드 폭만큼 **먹통** (2026-09-09 신고)
 *   ② 방향따라 스냅   → 왼쪽 드래그가 **작업 0인 구간**을 만든다 (2026-09-09 신고)
 *   ③ 가동 시간 환산  → ①② 는 사라졌지만 **어긋남이 영구히 누적**된다 (2026-09-10 신고)
 *   ④ 벽시계 + 클램프 ← 지금
 *
 * ③은 변위를 `가동 시간`으로 환산했다(`fillForward(origin, delta)`). 연속·단조라는 장점이 있었지만,
 * 커서는 벽시계 축에 있고 박스는 가동 시간 축에 있어 **축이 어긋난다** — 밴드를 가로지르면 박스가
 * 밴드 폭만큼 앞서가고, 밴드를 벗어나도 **그 차이가 돌아오지 않는다**(밴드 둘을 지나면 두 배).
 * 실측(출발 3h·순수 4h·밴드 [10h,20h)): 커서를 26h 까지 끌어도 어긋남이 −10h 로 고정이고
 * 커서가 박스 위에 있는 순간이 한 번도 없다.
 *
 * ④는 변위를 벽시계로 그대로 읽되, 결과가 밴드 안이면 `clampStartBeforeDowntime` 으로 **밴드 직전
 * 격자점에 세운다**. 같은 실측에서 커서 20h(밴드 탈출)부터 어긋남이 **0으로 복귀**한다 — 누적이 없다.
 * ①②도 재발하지 않는다: 밴드 안에서도 박스는 이미 밴드 앞으로 이동해 있고(①이 아니다),
 * 밴드 시작 **직전** 격자라 작업 0인 구간이 생기지 않는다(②가 아니다).
 *
 * 대가: **연속이 깨진다.** 밴드를 가로지르는 동안 박스가 밴드 앞에 멈춰 있다가(최대 밴드 폭만큼
 * 어긋남) 커서가 밴드를 벗어나는 순간 밴드 너머로 점프한다. 단조는 유지된다(뒤로 가지 않는다).
 * 2026-09-10 프로토타입(`prototypes/gantt-drag-cursor-models.html`)으로 ③과 나란히 비교한 뒤
 * 사용자가 ④를 선택했다 — 어긋남이 **경계가 있고 되돌아오는 것**이 영구 누적보다 낫다는 판단.
 *
 * @param snap 격자 간격. 화면은 `adjustShrink.SNAP_MS` 를 넘긴다.
 */
export const shiftDragStart = (origin, deltaMs, downtimes = [], snap = SNAP_DEFAULT_MS) => {
  if (!deltaMs) return origin;
  const dts = normalizeDowntimes(downtimes);
  return clampStartBeforeDowntime(origin + deltaMs, dts, origin, snap);
};

/**
 * 리사이즈로 끌고 있는 끝점을 **순수 소요 하한**으로 clamp 한다.
 *
 * `resolveResizeEdge` 의 최소 길이 검사는 **벽시계 span**(`mainEnd - minDuration`) 기준이다.
 * 휴무를 품은 작업은 span 이 순수 소요보다 훨씬 길어서(예: span 56h, 순수 16h) 순수가 이미 0이
 * 된 뒤에도 한참 더 끌린다 — 좌측 모서리를 줄이다 보면 시작이 종료를 지나쳐 **박스가 반대쪽으로
 * 넘어간 것처럼 보였다**(2026-09-09 신고). 남은 **작업량**으로 막아야 맞다.
 *
 * @param left `true` 면 좌측 모서리(끝 고정, 시작을 오른쪽으로) / `false` 면 우측(시작 고정)
 */
export const clampResizeEdge = (edge, { left, mainStart, mainEnd, minMs, downtimes = [] }) => {
  const dts = normalizeDowntimes(downtimes);
  return left
    ? Math.min(edge, fillBackward(mainEnd, minMs, dts))    // 순수 minMs 만 남기는 시작시각
    : Math.max(edge, fillForward(mainStart, minMs, dts));  // 순수 minMs 를 채우는 종료시각
};

/** `pushStartOutOfDowntime` 의 거울상 — 끝시각이 휴무 안이면 그 휴무의 **시작**으로 당긴다. */
export const pullEndOutOfDowntime = (end, downtimes = []) => {
  const hit = downtimes.find(dt => end > dt.s && end < dt.e);
  return hit ? hit.s : end;
};

/** 이동/리사이즈가 거부된 이유. `null` 이면 허용. */
export const BLOCK_NONSTOP_DOWNTIME = "nonstopDowntime";
/** `I` 가 뒤로 밀리다 휴무 **안**에서 시작하게 되는 지점 — 더는 못 민다(§7-39). */
export const BLOCK_IGNORE_START_DOWNTIME = "ignoreStartDowntime";

/**
 * 박스를 `newStart` 로 옮겼을 때의 **새 구간**을 분할타입 규칙으로 계산한다.
 *
 * @returns `{ s, e }` 또는 거부 시 `{ blocked: BLOCK_NONSTOP_DOWNTIME }`
 */
export const placeBox = (box, newStart, downtimes = []) => {
  const type = divideTypeOf(box);

  if (type === DIVIDE_IGNORE) {
    // 관통 — 길이 불변. **시작점이 휴무 밖이라는 전제**는 호출부(`applyDivideRules`)가 세운다.
    return { s: newStart, e: newStart + (box.e - box.s) };
  }

  if (type === DIVIDE_NONSTOP) {
    const end = newStart + (box.e - box.s);
    if (overlapsDowntime(newStart, end, downtimes)) {
      return { blocked: BLOCK_NONSTOP_DOWNTIME };
    }
    return { s: newStart, e: end };
  }

  // Y — 순수 소요를 유지하며 휴무를 건너뛰어 채운다.
  // `netMs`(로드 시 원래 좌표에서 구해 얹은 값)를 **우선** 쓴다. 없으면 현재 좌표에서 구하는데,
  // 그건 **아직 안 옮긴 박스에만 맞다** — 옮긴 뒤엔 늘어난 span 을 순수로 오인한다(위 주석 참고).
  const net = Number.isFinite(box.netMs) ? box.netMs : netDurationOf(box, downtimes);
  return { s: newStart, e: fillForward(newStart, net, downtimes) };
};

/**
 * `placeBox` 의 거울상 — **끝시각을 고정하고** 시작시각을 구한다.
 *
 * 왼쪽으로 미는 조작에서 이동 대상 **앞의** 박스들에 쓴다. 그쪽은 뒤 박스에 끝이 맞물려 있으므로,
 * 시작을 고정해 앞으로 채우면(=`placeBox`) 규칙이 길이를 줄일 때 **끝이 왼쪽으로 물러나 빈 공간이
 * 생긴다** — 2026-09-09 신고("선택 작업은 가만히 있고 앞 작업만 공간을 만들며 밀려난다").
 */
export const placeBoxByEnd = (box, newEnd, downtimes = []) => {
  const type = divideTypeOf(box);

  if (type === DIVIDE_IGNORE) {
    // 관통 — 길이 불변. 다만 **시작은 휴무 안에 놓일 수 없다**(§7-38).
    //
    // 뒤로 밀린 결과 시작이 밴드 안에 떨어지면 **거기서 멈춘다**(§7-39). 밴드 **시작**도 `I` 에게
    // 합법이라(`getStopInclusiveInfo(I) = {false,false}`) 통째로 왼쪽으로 당길 수도 있지만,
    // 그건 **밀림이 아니라 점프**다 — 2026-09-11 신고: 6h 짜리 `I` 가 뒤 작업을 2h 민 것만으로
    // 7h 를 건너뛰고 앞에 큰 틈을 남겼다. 밀림은 연속이어야 한다.
    //
    // 거부하면 `runClamped` 가 드래그를 **갈 수 있는 데까지**로 물려, `I` 의 시작이 밴드 끝에
    // 정확히 닿는 지점에서 멈춘다 — `N` 이 휴무를 만났을 때와 같은 처리다.
    //
    // ⚠️ 앞으로 미는 쪽(`applyDivideRules` 뒤쪽 루프)은 `placeBox` **호출 전에**
    // `pushStartOutOfDowntime` 으로 처리한다 — 거기는 시작이 끝을 결정하므로(Y의 `fillForward`)
    // 순서가 반대여야 한다. 이쪽은 끝이 고정이고 시작이 파생되므로 여기서 본다.
    const start = newEnd - (box.e - box.s);
    if (startsInsideDowntime(start, downtimes)) {
      return { blocked: BLOCK_IGNORE_START_DOWNTIME };
    }
    return { s: start, e: newEnd };
  }

  if (type === DIVIDE_NONSTOP) {
    const start = newEnd - (box.e - box.s);
    if (overlapsDowntime(start, newEnd, downtimes)) {
      return { blocked: BLOCK_NONSTOP_DOWNTIME };
    }
    return { s: start, e: newEnd };
  }

  const net = Number.isFinite(box.netMs) ? box.netMs : netDurationOf(box, downtimes);
  return { s: fillBackward(newEnd, net, downtimes), e: newEnd };
};

// ── JC(작업 교체) 전용 배치 ───────────────────────────────────────────────────
//
// ⚠️ **위의 `placeBox`/`placeBoxByEnd` 를 JC 에 쓰면 안 된다.** 둘은 **활동** 규칙이고,
// 특히 `placeBoxByEnd` 의 `I` 분기는 §7-39 에서 **거부**로 바꿨다(밀림은 연속이어야 한다는
// 조작감 정책). 그런데 **JC 에는 "밀림"이라는 개념이 없다** — 활동 좌표에서 한 방향으로
// 파생될 뿐이라 엔진 그대로 **밴드 시작으로 당겨야** 한다(§7-41 ⓔ).
//
// 또 하나 — JC 는 활동과 **가동 구간의 개폐 표가 다르다**(§7-40):
//
//   | | 활동 `getStopInclusiveInfo` | JC `getNonstopInclusiveInfo` |
//   |---|---|---|
//   | `Y`·`N` | `{true, false}` | `{true, false}` |
//   | **`I`** | `{false, false}` | **`{true, true}`** |
//
// `I` 만 가동 구간이 `[s, e]` 로 **닫힌다** → `I` JC 만 휴무 경계에서 시작할 수 있다.
// 아래 두 함수의 `bandOf`(경계 포함) / `bandStrict`(양 끝 제외) 차이가 그 표를 표현한다.

/** `t` 를 품은 휴무 — **시작 경계 포함**(`Y`·`N` 의 가동 구간이 `[.., 밴드시작)` 이라 못 선다). */
const bandOf = (t, dts) => dts.find(dt => t >= dt.s && t < dt.e);
/** `t` 를 품은 휴무 — **양 끝 제외**(`I` 의 가동 구간은 `[.., 밴드시작]` 이라 경계에 설 수 있다). */
const bandStrict = (t, dts) => dts.find(dt => t > dt.s && t < dt.e);
/**
 * `bandOf` 의 **뒤로 가는 판** — `t` 바로 **앞**에 가동 시간이 없게 만드는 휴무.
 * 앞으로 갈 때 `[밴드시작, 밴드끝)` 이 막는 것과 대칭으로, 뒤로 갈 때는 `(밴드시작, 밴드끝]` 이 막는다.
 * `t` 가 밴드 **끝 정각**이면 그 직전은 휴무라, `Y`·`N` 은 거기서 뒤로 채우기 시작할 수 없다.
 */
const bandBefore = (t, dts) => dts.find(dt => t > dt.s && t <= dt.e);

/**
 * **next JC** — 활동 **끝**에서 앞으로 `jcTime` 만큼. 엔진 `TimeSectionLists.forwardTimeSection`.
 *
 * @param anchorEnd 주인 활동의 **확정된** 끝시각. ⚠️ 스윕이 옮긴 중간값을 넣으면 안 된다 —
 *                  최종 끝에서 파생해야 한다(§7-41, 두 번 밟은 함정).
 */
export const placeJcForward = (anchorEnd, tp, jcTime, downtimes = []) =>
  placeJcForwardIn(anchorEnd, tp, jcTime, normalizeDowntimes(downtimes));

/** `placeJcForward` 의 알맹이 — **이미 정규화된** 휴무를 받는다(§7-49 핫패스). */
const placeJcForwardIn = (anchorEnd, tp, jcTime, dts) => {
  let start = anchorEnd;

  if (tp === DIVIDE_IGNORE) {
    // 경계(밴드 시작)는 합법이라 거기서 시작해 **관통**한다 — §7-40 원인 ① 의 `I` 쪽.
    const inside = bandStrict(start, dts);
    if (inside) start = inside.e;
    return { s: start, e: start + jcTime };
  }

  // `Y`·`N` 은 가동 구간 끝이 **배제**라 밴드 시작에서 시작하지 못하고 휴무 뒤로 밀린다(원인 ①).
  const band = bandOf(start, dts);
  if (band) start = band.e;

  if (tp === DIVIDE_NONSTOP) {
    // 자기를 **통째로** 담는 첫 가동 구간까지 건너뛴다(원인 ③ — `TimeSectionWorkerN:295`).
    // `start` 는 단조 증가하므로 인덱스를 앞으로만 밀면 된다(휴무마다 `find` 를 돌지 않는다).
    let i = 0;
    while (overlapsDowntime(start, start + jcTime, dts)) {
      while (i < dts.length && dts[i].s < start) i += 1;
      if (i >= dts.length) break;
      start = dts[i].e;
      i += 1;
    }
    return { s: start, e: start + jcTime };
  }

  return { s: start, e: fillForward(start, jcTime, dts) };   // Y — 휴무를 건너뛰며 채운다
};

/**
 * **prev JC** — 활동 **시작**에서 뒤로 `jcTime` 만큼. 엔진 `backwardTimeSection`.
 *
 * ⚠️ `I` 는 자연 시작이 휴무 **안**이면 **밴드 시작으로 당긴다**(`TimeSectionWorkerBackwardI:222`).
 * 길이는 보존되고 활동과는 `휴무폭 − jcTime` 만큼 벌어진다 — 그게 엔진의 답이다(원인 ④).
 * **여기서 §7-39 처럼 거부하면 안 된다**(그건 이웃 밀기 정책이고 JC 에는 해당 없다).
 */
export const placeJcBackward = (anchorStart, tp, jcTime, downtimes = []) =>
  placeJcBackwardIn(anchorStart, tp, jcTime, normalizeDowntimes(downtimes));

/** `placeJcBackward` 의 알맹이 — **이미 정규화된** 휴무를 받는다(§7-49 핫패스). */
const placeJcBackwardIn = (anchorStart, tp, jcTime, dts) => {
  if (tp === DIVIDE_IGNORE) {
    const natural = anchorStart - jcTime;
    const band = bandStrict(natural, dts);
    const start = band ? band.s : natural;
    return { s: start, e: start + jcTime };
  }

  // ⚠️ 앞으로 갈 때 `bandOf` 로 미는 것과 **대칭**이다 — `Y`·`N` 은 가동 구간 끝이 배제라
  // 밴드 **끝 정각**에서 뒤로 채우기 시작하지 못한다. 그 밴드의 시작으로 물러난다.
  // 이걸 빼면 `fillBackward` 가 끝을 `anchorStart` 로 붙여 버려 **엔진과 갈린다**
  // (2026-09-11 — `jcPlacement.test.js` 가 잡았다. `fillBackward` 는 **활동** 규칙이라
  // 끝이 앵커에 고정이지만, JC 는 `rearrangeTimeSection` 이 **마지막 가동 구간의 끝**을 준다).
  const back = bandBefore(anchorStart, dts);
  const from = back ? back.s : anchorStart;

  if (tp === DIVIDE_NONSTOP) {
    // 담을 수 있는 가동 구간까지 **앞으로** 건너뛴다 — 위 거울상. `end` 는 단조 감소한다.
    let end = from;
    let i = dts.length - 1;
    while (overlapsDowntime(end - jcTime, end, dts)) {
      while (i >= 0 && dts[i].s >= end) i -= 1;
      if (i < 0) break;
      end = dts[i].s;
      i -= 1;
    }
    return { s: end - jcTime, e: end };
  }

  return { s: fillBackward(from, jcTime, dts), e: from };                 // Y
};

/**
 * JC 박스 하나를 **주인 활동의 확정된 좌표**에서 다시 파생한다.
 *
 * `box.jcTime`(= 서버의 `jc_time`, 마스터에서 온 순수 소요)을 쓴다 — 좌표에서 역산하지 않는다.
 * 좌표는 틀릴 수 있어도 `jc_time` 은 마스터 값이라 면역이다(§7-41 0-b).
 * 값이 없으면 현재 span 을 순수로 본다(옛 데이터 방어).
 */
export const placeJcOf = (jcBox, owner, downtimes = []) =>
  placeJcAt(jcBox, owner, normalizeDowntimes(downtimes));

/** `placeJcOf` 의 알맹이 — **이미 정규화된** 휴무를 받는다(§7-49 핫패스). */
const placeJcAt = (jcBox, owner, dts) => {
  const tp = divideTypeOf(jcBox);
  const time = Number.isFinite(jcBox?.jcTime) ? jcBox.jcTime : (jcBox.e - jcBox.s);
  return jcBox.jcKind === "prev"
    ? placeJcBackwardIn(owner.s, tp, time, dts)
    : placeJcForwardIn(owner.e, tp, time, dts);
};

/**
 * JC 를 파생한 뒤 앞 그룹을 덮으면 활동을 더 미는데, 그 **재시도 상한**이다.
 * 한 번 밀 때마다 부족분이 줄어 보통 1~2 회에 끝난다(휴무를 새로 만나면 한 번 더).
 * 상한에 닿으면 마지막 결과를 그대로 쓴다 — 미리보기라 수렴 실패가 치명적이지 않고,
 * 무한 루프가 프레임을 멈추는 쪽이 훨씬 나쁘다.
 */
const JC_FIT_ATTEMPTS = 6;

/** `firstFit*` 의 휴무 건너뛰기 상한 — 휴무 뒤는 결국 전부 가동이라 실제로는 몇 번이면 끝난다. */
const FIRST_FIT_GUARD = 64;

/**
 * 박스 목록을 **그룹**(prev JC · 활동 · next JC)으로 묶는다. 입력 순서(시작시각 오름차순)를 따른다.
 *
 * `ownerKeyOf` 가 없으면 박스마다 제 그룹이 되어 **옛 동작과 완전히 같아진다**
 * (`jcKind` 도 보지 않는다) — 기존 호출부·테스트의 계약을 그대로 둔다.
 */
const groupByOwner = (boxes, ownerKeyOf) => {
  const groups = [];
  const index = new Map();

  boxes.forEach((box, i) => {
    const member = { box, index: i };
    const key = ownerKeyOf ? ownerKeyOf(box.id) : i;

    let group = index.get(key);
    if (!group) {
      group = { main: null, prevJc: null, nextJc: null, members: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.members.push(member);

    const kind = ownerKeyOf ? box.jcKind : null;
    if (kind === "prev") group.prevJc = member;
    else if (kind === "next") group.nextJc = member;
    else group.main = member;
  });

  groups.forEach(group => {
    // 주인을 잃은 JC(행 일부만 남은 미리보기 등)는 **자기가 주인**이 되어 활동 규칙을 탄다.
    // 좌표 없이 사라지는 것보다 옛 동작으로 그려지는 쪽이 낫다.
    if (!group.main) {
      group.main = group.prevJc ?? group.nextJc;
      if (group.prevJc === group.main) group.prevJc = null;
      if (group.nextJc === group.main) group.nextJc = null;
    }
    // 스윕(규칙 적용 **전**) 기준 그룹 경계 — 맞닿음 판정에 쓴다.
    group.sweptStart = Math.min(...group.members.map(member => member.box.s));
    group.sweptEnd = Math.max(...group.members.map(member => member.box.e));
  });

  return groups;
};

/**
 * 한 그룹을 **앞으로** 놓는다 — ⓐ 활동 → ⓑ JC 파생 → ⓒ 앞 그룹을 덮으면 활동을 더 밀기.
 *
 * ⓒ가 필요한 이유: prev JC 는 활동을 놓고 나서야 길이가 정해지는데(`I` 는 밴드 시작으로 당겨지고
 * `Y` 는 휴무를 품어 늘어난다), 그게 앞 그룹을 덮을 수 있다. 엔진은 자유 구간을 미리 JC 시간만큼
 * 깎아서 이 자리를 **예약**해 둔다(`FreeTimeSectionSupplier.getJcAppliedTimeSection:343`) —
 * 화면도 같은 결과를 내려면 활동을 그만큼 더 밀어야 한다(§7-41 케이스 1).
 *
 * @param floorTs 앞 그룹의 **규칙 적용 후** 끝. prev JC 가 이보다 앞설 수 없다.
 */
const fitGroupForward = (group, wanted, floorTs, dts) => {
  const main = group.main.box;
  const nonstop = divideTypeOf(main) === DIVIDE_NONSTOP;

  const place = (want) => {
    // **`Y` 와 `I` 둘 다** 시작점을 휴무 밖으로 민다(§7-38). `N` 은 밀지 않는다 — 겹치면 거부다.
    const start = nonstop ? want : pushStartOutOfDowntime(want, dts);
    const placed = placeBox(main, start, dts);
    if (placed.blocked) return { blocked: placed.blocked, box: main };

    const prevJc = group.prevJc ? placeJcAt(group.prevJc.box, placed, dts) : null;
    const nextJc = group.nextJc ? placeJcAt(group.nextJc.box, placed, dts) : null;
    return {
      main: placed, prevJc, nextJc, anchor: start,
      end: nextJc ? Math.max(placed.e, nextJc.e) : placed.e
    };
  };

  const fits = (out) => !out.prevJc || !Number.isFinite(floorTs) || out.prevJc.s >= floorTs;

  const first = place(wanted);
  if (first.blocked || fits(first)) return first;

  return bisectFit(place, fits, tightForward(floorTs), first, floorTs - first.prevJc.s, 1);
};

/**
 * **들어가는 가장 이른 자리**를 찾는다 — 부족분만큼 미는 것만으로는 **과하게 민다**(§7-48).
 *
 * 이웃의 prev JC 가 휴무를 가로지르면 그 시작이 밴드 앞까지 내려가서 부족분이 `밴드 폭 + α` 로
 * 잡힌다. 그런데 그만큼 밀고 나면 JC 는 밴드 밖으로 나와 **더 이상 그만한 자리를 필요로 하지
 * 않는다.** 처음 통과한 값을 그냥 쓰면 밴드 폭만큼(실측 48h) 빈칸이 생긴다
 * (2026-09-15 신고: "뜬금없이 간격을 벌리면서 오른쪽으로 배치돼").
 *
 * `placeBox`·`placeJcOf` 가 모두 시작시각에 **단조**라 이분 탐색이 성립한다 —
 * `runClamped` 와 같은 논리다. ms 단위까지 좁힌다(격자는 호출부가 이미 맞춰 놨다).
 */
/**
 * **딱 맞게 들어갔나** — 이러면 더 좋은 답이 없으므로 이분 탐색을 건너뛴다(§7-49 핫패스).
 *
 * 밀려난 이웃의 흔한 경우가 정확히 여기다: 활동을 `cursor` 에 놓으면 prev JC 는 `cursor - jcTime`
 * 에서 시작하고, 부족분(`jcTime`)만큼 밀면 JC 가 **정확히 `cursor` 에서** 시작한다. 휴무를
 * 가로지르지 않는 한 이 한 번으로 끝난다 — 오른쪽 슬라이드에서 밀리는 그룹 **전부**가 이 경우다.
 */
const tightForward = (floorTs) => (out) => out.prevJc && out.prevJc.s === floorTs;
const tightBackward = (ceilingTs) => (out) => out.nextJc && out.nextJc.e === ceilingTs;

const bisectFit = (place, fits, tight, first, initialStep, dir) => {
  let lo = first.anchor;                        // 안 들어가는 것으로 확인된 가장 먼 지점
  let best = null;
  let step = Math.max(1, initialStep);

  for (let i = 0; i < JC_FIT_ATTEMPTS; i += 1) {
    const candidate = place(lo + dir * step);
    if (candidate.blocked) return candidate;
    if (fits(candidate)) {
      if (tight(candidate)) return candidate;   // 더 좋은 답이 없다 — 탐색 생략
      best = candidate;
      break;
    }
    lo = candidate.anchor;
    step *= 2;                                  // 한 번에 못 넘으면 배로 — 보통 첫 번에 끝난다
  }
  if (!best) return first;                      // 끝내 못 찾으면 원래 결과를 쓴다(미리보기다)

  let hi = best.anchor;
  while (Math.abs(hi - lo) > 1) {
    const mid = lo + Math.trunc((hi - lo) / 2);
    const candidate = place(mid);
    if (candidate.blocked) { lo = mid; continue; }
    if (fits(candidate)) { hi = mid; best = candidate; } else lo = mid;
  }
  return best;
};

/** `fitGroupForward` 의 거울상 — 끝을 고정하고 뒤로 채운다. next JC 가 뒤 그룹을 덮으면 당긴다. */
const fitGroupBackward = (group, wanted, ceilingTs, dts) => {
  const main = group.main.box;
  const split = divideTypeOf(main) === DIVIDE_SPLIT;

  const place = (want) => {
    // ⚠️ **끝을 당기는 건 `Y` 만이다.** `I` 는 종료가 휴무 안이어도 된다(엔진
    // `forwardDateTimePeriodI` 가 `휴무시작 + remain` 을 종료로 돌려준다).
    const end = split ? pullEndOutOfDowntime(want, dts) : want;
    const placed = placeBoxByEnd(main, end, dts);
    if (placed.blocked) return { blocked: placed.blocked, box: main };

    const prevJc = group.prevJc ? placeJcAt(group.prevJc.box, placed, dts) : null;
    const nextJc = group.nextJc ? placeJcAt(group.nextJc.box, placed, dts) : null;
    return {
      main: placed, prevJc, nextJc, anchor: end,
      start: prevJc ? Math.min(placed.s, prevJc.s) : placed.s
    };
  };

  const fits = (out) => !out.nextJc || !Number.isFinite(ceilingTs) || out.nextJc.e <= ceilingTs;

  const first = place(wanted);
  if (first.blocked || fits(first)) return first;

  // 앞 루프와 같은 이유로 **되밀어 준다** — 초과분도 과대평가다(§7-48).
  return bisectFit(place, fits, tightBackward(ceilingTs), first, first.nextJc.e - ceilingTs, -1);
};

/**
 * **재배치된 행 전체**에 규칙을 적용한다. 이동 대상뿐 아니라 `Shrink`/`Slide` 스윕으로
 * **밀려난 이웃**도 대상이다 — N 타입 이웃이 밀려서 휴무에 걸치는 경우를 잡아야 한다.
 *
 * **기준 박스(`isAnchor`, = 사용자가 잡은 것)를 축으로 양쪽으로 갈라 채운다** — `applySlide` 의
 * 두 루프와 같은 구조다.
 *
 *   기준 박스부터 **뒤쪽**: 시작을 고정하고 앞으로 채운다(`placeBox`).
 *     ① 앞 박스의 **규칙 적용 후** 끝시각 뒤로 민다(`cursor`)
 *     ② Y 박스의 시작이 휴무 안이면 밴드 **끝**으로 민다(`pushStartOutOfDowntime`)
 *     ③ 그 시작에서 끝시각을 다시 잡는다
 *
 *   기준 박스 **앞쪽**: 끝을 고정하고 뒤로 채운다(`placeBoxByEnd`). 간격은 그대로 둔다
 *     (`Math.min(box.e, limit)` — 겹칠 때만 당긴다).
 *
 * ⚠️ **앞쪽까지 앞으로 채우면 빈 공간이 생긴다.** 왼쪽으로 밀면 앞 박스가 밴드 밖으로 나가면서
 * 규칙이 길이를 줄이는데, 시작이 고정돼 있으면 **끝이 왼쪽으로 물러나** 기준 박스와의 사이가
 * 벌어진다. 그러면 기준 박스는 제자리에 머물고 앞 작업만 공간을 만들며 밀려난 것처럼 보인다
 * (2026-09-09 신고). 끝을 붙잡고 시작을 구하면 맞물림이 유지된다.
 *
 * 조작 방향은 여기서 보지 않는다 — 드래그 목표는 `shiftDragStart` 가 이미 휴무 밖으로 클램프해서
 * 넘겨준다. 여기는 **밀려난 이웃까지 포함해 배치를 합법으로 만드는** 일만 한다.
 *
 * ⚠️ **①②가 없으면 시작시각이 휴무 안에 들어간다** — 2026-09-09 사용자 신고.
 * 스윕(`applyShrink`/`applySlide`)은 **규칙 적용 전 길이**로 이웃을 밀기 때문에, 밀려난 이웃의
 * 시작이 앞 박스의 *옛* 끝에 붙는다. 앞 박스가 규칙으로 늘어나면 그 값은 이미 틀렸고, 하필 그
 * 지점이 휴무 안이면 **시작이 휴무에 박힌 채로 남는다**. 옛 구현은 시작을 스윕 값 그대로 쓰고
 * 끝시각만 다시 계산해서 이걸 놓쳤다("미리보기라 한 번만 적용한다"로 정당화했지만, 재수렴이
 * 아니라 **순서대로 한 번 훑는 것만으로 정확히 풀리는 문제**였다).
 *
 * 엔진도 같은 불변식을 지킨다 — `Y`는 시작·종료 모두 가동 시간에만 놓인다
 * (`DateTimePeriods.forwardDateTimePeriodY`, 2026-09-09 확인). `I`는 예외라 밀지 않는다.
 *
 * ## JC(작업 교체)는 박스가 아니라 **활동의 파생값**이다 (§7-43, 2026-09-15)
 *
 * `ownerKeyOf` 를 넘기면 박스들을 **그룹**(prev JC · 활동 · next JC)으로 묶어 아래 순서로 처리한다:
 *
 *   ⓐ **활동**을 자기 분할타입 제약으로 놓는다 (`placeBox`/`placeBoxByEnd`)
 *   ⓑ **JC** 를 그 확정 좌표에서 다시 파생한다 (`placeJcOf`) — 평행이동이 아니다
 *   ⓒ 파생된 JC 가 앞 그룹을 덮으면 **활동을 더 밀어** 자리를 만든다 (`JC_FIT_ATTEMPTS`)
 *
 * **활동 제약이 언제나 먼저다.** 옛 구현은 JC 를 독립 박스로 훑어서, 왼쪽 슬라이드 때
 * 활동만 `pushStartOutOfDowntime` 으로 제자리에 되밀리고 **JC 는 끌려간 자리에 남아 찢어졌다**
 * (2026-09-15 신고: "작업은 안 움직이고 jc만 분리돼서 움직인다"). 게다가 휴무 안으로 들어간
 * `Y` JC 는 `netDurationOf` 가 순수 0 을 주어 **폭 0 으로 붕괴**했다.
 *
 * `ownerKeyOf` 를 생략하면 모든 박스가 각자 그룹이 되어 **옛 동작 그대로**다.
 *
 * @param ownerKeyOf 박스 id → 그룹 키(= 주인 활동의 id). 화면은 `adjustShrink.groupKeyOf` 를 넘긴다.
 * @returns `{ boxes }` 또는 거부 시 `{ blocked, box }` (box = 위반한 박스, 메시지에 쓴다)
 */
export const applyDivideRules = (
  boxes = [], downtimes = [], isAnchor = null, ownerKeyOf = null
) => {
  // ⚠️ **휴무가 없어도 돌린다**(§7-54, 2026-09-16 신고). 한때 `dts.length === 0` 이면 곧바로
  // 반환했는데, 그러면 **길이 정규화까지 건너뛴다** — 휴무를 품어 span 이 부푼 `Y` 작업을
  // **휴무 없는 자원으로 옮기면 부푼 채로 남았다**(순수 50h 인데 98h). 이 모듈의 계약은
  // "`netMs` 가 이동 불변량이고 span 은 **대상 행의 달력**이 정한다" 이므로(§7-29),
  // 대상 행에 휴무가 없다는 것도 **하나의 달력**이지 "할 일 없음"이 아니다.
  // 빈 배열이면 `fillForward`/`pushStartOutOfDowntime` 가 전부 항등이라 결과는 곧 `netMs` 다.
  const dts = normalizeDowntimes(downtimes);
  const groups = groupByOwner(boxes, ownerKeyOf);
  const found = isAnchor
    ? groups.findIndex(group => group.members.some(member => isAnchor(member.box)))
    : -1;
  const anchor = found > 0 ? found : 0;
  const next = boxes.slice();
  const same = (placed, box) => placed.s === box.s && placed.e === box.e;
  const write = (member, placed) => {
    if (!member) return;
    next[member.index] = same(placed, member.box)
      ? member.box
      : { ...member.box, s: placed.s, e: placed.e };
  };

  // ── 기준 박스부터 뒤쪽: 시작을 고정하고 **앞으로** 채운다 ──
  // ⚠️ **아래 앞쪽 루프와 같은 규칙이다 — 맞닿아 있었으면 규칙 후에도 맞닿아야 한다.**
  // 규칙이 기준 박스를 **줄이는** 경우가 문제다(휴무를 품고 있다가 밴드 밖으로 나갈 때).
  // 스윕은 규칙 **전** 길이(휴무를 삼킨 긴 span)로 이웃을 밀어냈는데, 여기서 `Math.max` 로만
  // 보면 이웃이 그 먼 좌표에 그대로 남아 **기준 박스 뒤에 빈 공간이 생긴다**
  // (2026-09-10 신고: 리사이즈로 휴무까지 늘린 작업을 오른쪽으로 slide → 뒤 작업들이 안 붙음).
  // 떨어져 있던 박스는 그대로 둔다(`Math.max`) — 원래 간격까지 없애면 안 된다.
  //
  // ⚠️ **되당김은 `homeS`(스윕 전 자리)에서 멈춘다 — 밀려난 만큼만 되돌아온다.**
  // 기준 박스가 휴무를 삼켜 span 이 부풀면 스윕이 뒤 박스를 그 부풀린 길이로 밀어내는데, 규칙이
  // 기준 박스를 순수 소요로 줄일 때 그대로 따라오면 **원래 자리보다 앞으로 끌려간다**
  // (2026-09-10 신고: 좌드래그 한 번에 뒤 작업 무리가 통째로 앞당겨짐 — §7-29c).
  // `homeS` 가 없으면 제약 없음(`-Infinity`)이라 옛 동작 그대로다.
  let cursor = -Infinity;
  let sweptEnd = -Infinity;                                     // 규칙 적용 **전** 앞 그룹의 끝
  let anchorStart = Infinity;                                   // 규칙 적용 **후** 기준 그룹의 시작
  for (let i = anchor; i < groups.length; i += 1) {
    const group = groups[i];
    const abutting = group.sweptStart <= sweptEnd;              // 스윕 결과에서 맞닿아 있었나
    const home = group.main.box.homeS;
    const floor = Number.isFinite(home) ? home : -Infinity;
    const wanted = abutting ? Math.max(cursor, floor) : Math.max(group.main.box.s, cursor);

    const fit = fitGroupForward(group, wanted, cursor, dts);
    if (fit.blocked) return { blocked: fit.blocked, box: fit.box };

    sweptEnd = group.sweptEnd;
    cursor = fit.end;
    if (i === anchor) anchorStart = fit.prevJc ? Math.min(fit.main.s, fit.prevJc.s) : fit.main.s;
    write(group.main, fit.main);
    write(group.prevJc, fit.prevJc);
    write(group.nextJc, fit.nextJc);
  }

  // ── 기준 박스 앞쪽: 끝을 고정하고 **뒤로** 채운다 ──
  // ⚠️ **스윕에서 맞닿아 있었으면 규칙 적용 후에도 맞닿아야 한다.** 규칙은 기준 박스를 밴드 밖으로
  // 밀거나(앞으로) 길이를 줄이는데, 그때 뒤따르는 박스들이 스윕 좌표에 남아 있으면 그 사이가
  // 벌어진다 — "노란색 작업들 사이에 간격이 생긴다"(2026-09-09 신고).
  // 떨어져 있던 박스는 그대로 둔다(`Math.min`) — 원래 간격까지 없애면 안 된다.
  let limit = anchorStart;                                      // 규칙 적용 **후** 다음 그룹 시작
  let swept = groups[anchor] ? groups[anchor].sweptStart : Infinity;   // 규칙 적용 **전**
  for (let i = anchor - 1; i >= 0; i -= 1) {
    const group = groups[i];
    const abutting = group.sweptEnd >= swept;                   // 스윕 결과에서 맞닿아 있었나
    // 위 루프의 거울상 — 되밀림도 `homeE`(스윕 전 끝)에서 멈춘다. 밀려난 만큼만 되돌아온다.
    // (신고는 뒤쪽에서만 나왔지만 두 루프는 거울상이라 같이 건다 — §7-29b 교훈.)
    const home = group.main.box.homeE;
    const ceiling = Number.isFinite(home) ? home : Infinity;
    const wanted = abutting ? Math.min(limit, ceiling) : Math.min(group.main.box.e, limit);

    const fit = fitGroupBackward(group, wanted, limit, dts);
    if (fit.blocked) return { blocked: fit.blocked, box: fit.box };

    limit = fit.start;
    swept = group.sweptStart;
    write(group.main, fit.main);
    write(group.prevJc, fit.prevJc);
    write(group.nextJc, fit.nextJc);
  }

  return { boxes: next };
};

/**
 * `N` 이 **통째로 들어가는** 첫 가동 구간의 시작(≥ `from`). 엔진 `TimeSectionWorkerN:295` 와 같다 —
 * 자투리 가동 시간은 건너뛰고 자기를 담을 수 있는 구간까지 간다.
 */
const firstFitForward = (from, net, dts) => {
  // `start` 는 단조 증가하므로 인덱스를 앞으로만 밀면 된다(휴무마다 `find` 를 돌지 않는다 —
  // 루프 안 클로저는 `no-loop-func` 경고 대상이기도 하다).
  let start = from;
  let i = 0;
  for (let guard = 0; guard < FIRST_FIT_GUARD; guard += 1) {
    while (i < dts.length && dts[i].e <= start) i += 1;   // 이미 지나친 휴무
    if (i >= dts.length) return start;
    if (dts[i].s >= start + net) return start;            // 다음 휴무 전에 끝난다
    start = dts[i].e;                                     // 겹친다 → 그 휴무 뒤로
    i += 1;
  }
  return start;
};

/** 거울상 — `N` 이 들어가는 첫 가동 구간의 **끝**(≤ `from`). */
const firstFitBackward = (from, net, dts) => {
  let end = from;
  let i = dts.length - 1;
  for (let guard = 0; guard < FIRST_FIT_GUARD; guard += 1) {
    while (i >= 0 && dts[i].s >= end) i -= 1;             // 아직 지나지 않은(뒤쪽) 휴무
    if (i < 0) return end;
    if (dts[i].e <= end - net) return end;                // 앞 휴무 뒤에서 끝난다
    end = dts[i].s;                                       // 겹친다 → 그 휴무 앞으로
    i -= 1;
  }
  return end;
};

/**
 * **전체 계획 당기기** — 기준 작업 한쪽을 여유 공간 없이 압착한다(§7-57).
 *
 * 범위는 **행 하나**다(old 가 백엔드로 `resourceCd` + 그 행의 활동만 보냈다 — "전체 계획"이라는
 * 이름과 달리 전 자원이 아니다). 기준 작업 자신은 움직이지 않는다.
 *
 *   ⓐ 각 그룹을 앞(뒤) 그룹 끝에 붙인다
 *   ⓑ **기존 분할타입 규칙으로 합법화**한다 — `fitGroupForward`/`Backward` 를 그대로 쓴다.
 *      JC 는 `placeJcOf` 로 재파생되고, 자리가 모자라면 §7-48 의 이분 탐색이 활동을 민다.
 *   ⓒ `N` 이 걸리면 **통째로 들어가는 첫 가동 구간**까지만 당긴다. 그 자리가 원래보다 뒤면
 *      **움직이지 않는다** — 당기기는 미는 조작이 아니다.
 *   ⓓ 못 당긴 작업에서 **멈추지 않고** 다음 작업을 계속 당긴다. 단 커서는 그 작업의 **실제 끝**을
 *      이어받아 앞지르지 않는다.
 *
 * ⚠️ **`Y` 는 완전히 안 붙을 수 있다.** 휴무를 만나면 그만큼 늘어난다 — 그 구간엔 아무도 일하지
 * 않으므로 그게 맞는 답이다. 붙지 않은 것처럼 보이는 자리는 빗금이 설명한다(§7-46).
 *
 * ⚠️ **`unitsToBoxes` 를 거치지 않는다.** 그 함수는 이동 대상 외 전부에 `homeS`/`homeE`(밀려나기
 * 전 자리) 바닥을 붙이는데(§7-29c), 당기기는 **모든 그룹이 의도적으로 움직이는** 조작이라 그
 * 바닥이 걸리면 아무것도 안 당겨진다. 여기서는 박스 좌표를 직접 쓴다.
 *
 * @param direction "right" = 기준의 **오른쪽**을 왼쪽으로 / "left" = 왼쪽을 오른쪽으로
 * @returns `{ boxes, movedKeys, stuckKeys }` — `stuckKeys` 는 `N` 이라 못 당긴 그룹(메시지에 쓴다)
 */
export const pullRow = ({
  boxes = [], downtimes = [], anchorKey = null, direction = "right", ownerKeyOf = null
}) => {
  const dts = normalizeDowntimes(downtimes);
  const groups = groupByOwner(boxes, ownerKeyOf);
  const anchor = anchorKey == null
    ? -1
    : groups.findIndex(group => group.members.some(
      member => (ownerKeyOf ? ownerKeyOf(member.box.id) : member.box.id) === anchorKey
    ));

  const next = boxes.slice();
  const movedKeys = new Set();
  const stuckKeys = new Set();
  if (anchor < 0) return { boxes: next, movedKeys, stuckKeys };

  const netOf = (box) => (Number.isFinite(box.netMs) ? box.netMs : netDurationOf(box, dts));
  const write = (member, placed) => {
    if (!member || !placed) return;
    if (placed.s === member.box.s && placed.e === member.box.e) return;
    next[member.index] = { ...member.box, s: placed.s, e: placed.e };
    movedKeys.add(ownerKeyOf ? ownerKeyOf(member.box.id) : member.box.id);
  };
  const commit = (group, fit) => {
    write(group.main, fit.main);
    write(group.prevJc, fit.prevJc);
    write(group.nextJc, fit.nextJc);
  };
  const keyOf = (group) => (ownerKeyOf ? ownerKeyOf(group.main.box.id) : group.main.box.id);

  if (direction === "right") {
    let cursor = groups[anchor].sweptEnd;
    for (let i = anchor + 1; i < groups.length; i += 1) {
      const group = groups[i];
      let fit = fitGroupForward(group, cursor, cursor, dts);
      if (fit.blocked) {
        // `N` — 담을 수 있는 첫 가동 구간까지만. 원래보다 뒤면 그대로 둔다.
        const want = firstFitForward(cursor, netOf(group.main.box), dts);
        fit = want < group.main.box.s ? fitGroupForward(group, want, cursor, dts) : null;
      }
      if (!fit || fit.blocked) {
        stuckKeys.add(keyOf(group));
        cursor = group.sweptEnd;
        continue;
      }
      commit(group, fit);
      cursor = fit.end;
    }
    return { boxes: next, movedKeys, stuckKeys };
  }

  let limit = groups[anchor].sweptStart;
  for (let i = anchor - 1; i >= 0; i -= 1) {
    const group = groups[i];
    let fit = fitGroupBackward(group, limit, limit, dts);
    if (fit.blocked) {
      const want = firstFitBackward(limit, netOf(group.main.box), dts);
      fit = want > group.main.box.e ? fitGroupBackward(group, want, limit, dts) : null;
    }
    if (!fit || fit.blocked) {
      stuckKeys.add(keyOf(group));
      limit = group.sweptStart;
      continue;
    }
    commit(group, fit);
    limit = fit.start;
  }
  return { boxes: next, movedKeys, stuckKeys };
};
